import { Outlet, useParams } from "react-router";
import t from "@/lang/lang";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  NavigationGroup,
  NavigationItem,
  ProcessPage,
  ProcessPageMain,
  ProcessPageMainContent,
  ProcessPageSidebar,
  ProcessPageSidebarFooter,
  ProcessPageSidebarNavigation,
  ProcessPageSkeleton,
} from "@/shared/bsm/core/process/components/ui/process-page";
import { DynamicsSalesLinksSidebarGroup } from "@/routes/processes/[processId]/_components/dynamics-sales-links-sidebar-group";
import { GSMProcessKind } from "@/services/backend/processes/types/process-kind";
import {
  Calculations,
  Certificates,
  Commitments,
  Confirmations,
  Contacts,
  Offers,
  Orders,
  Overview,
  WorkOrders,
} from "@/shared/bsm/core/process/components/ui/process-page-navigation-item";
import { ProcessKind } from "@bitory-gmbh/bsm";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { MainPageContent } from "@/routes/_components/main-page";
import { Alert403 } from "@/shared/components/alerts/alert-403";
import { ProcessActionsSidebarGroup } from "../_components/process-actions-sidebar-group";

export default function RLTRoute() {
  const { processId } = useParams();
  const {
    data: process,
    isLoading,
    error,
  } = useProcessesProcessShowQuery({
    id: processId ?? "",
  });

  const sidebarNavigation = useSidebarNavigation(process?.processKind);

  const canAccess = useFeatureAuth("gsm.rlt");
  if (!canAccess) {
    return (
      <MainPageContent>
        <Alert403 />
      </MainPageContent>
    );
  }

  return (
    <ProcessPage>
      {isLoading && <ProcessPageSkeleton />}
      {error && (
        <ProcessPageMain>
          <ProcessPageMainContent>
            <RTKQueryErrorAlert error={error} />
          </ProcessPageMainContent>
        </ProcessPageMain>
      )}
      {process && (
        <>
          <ProcessPageSidebar className="pt-16">
            <ProcessPageSidebarNavigation groups={sidebarNavigation} />
            <ProcessPageSidebarFooter>
              <ProcessActionsSidebarGroup process={process} />
              <DynamicsSalesLinksSidebarGroup processId={process.id} />
            </ProcessPageSidebarFooter>
          </ProcessPageSidebar>
          <Outlet />
        </>
      )}
    </ProcessPage>
  );
}

function useSidebarNavigation(processKind?: ProcessKind): NavigationGroup[] {
  const canAccessCalculation = useFeatureAuth("gsm.rlt.calculation");
  const canAccessOffer = useFeatureAuth("gsm.rlt.offer");
  const canAccessOrder = useFeatureAuth("gsm.rlt.order");
  const canAccessConfirmation = useFeatureAuth("gsm.rlt.confirmation");
  const canAccessCommitment = useFeatureAuth("gsm.rlt.commitment");
  const canAccessWorkOrder = useFeatureAuth("gsm.rlt.workorder");
  const canAccessWorkOrderPlanning = useFeatureAuth(
    "gsm.rlt.workorder.planning",
  );
  const canAccessWorkOrderExecution = useFeatureAuth(
    "gsm.rlt.workorder.execution",
  );
  const canAccessWorkOrderCompletion = useFeatureAuth(
    "gsm.rlt.workorder.completion",
  );
  const canAccessCertificate = useFeatureAuth("gsm.rlt.certificate");

  const itemFilter = (item: NavigationItem) => {
    switch (item.to) {
      case "calculations":
        return canAccessCalculation;
      case "offers":
        return canAccessOffer;
      case "orders":
        return canAccessOrder;
      case "confirmations":
        return canAccessConfirmation;
      case "commitments":
        return canAccessCommitment;
      case "work-orders":
        return canAccessWorkOrder;
      case "work-orders/planning":
        return canAccessWorkOrderPlanning;
      case "work-orders/execution":
        return canAccessWorkOrderExecution;
      case "work-orders/completion":
        return canAccessWorkOrderCompletion;
      case "certificates":
        return canAccessCertificate;
      default:
        return false;
    }
  };

  if (!processKind) {
    return [];
  }

  switch (processKind) {
    case GSMProcessKind.Full:
      return [
        {
          label: t("Allgemein"),
          items: [Overview, Contacts],
        },
        {
          label: t("Meilensteine"),
          items: [
            Calculations,
            Offers,
            Orders,
            Confirmations,
            Commitments,
            {
              ...WorkOrders,
              children: WorkOrders.children?.filter(itemFilter),
            },
            Certificates,
          ].filter(itemFilter),
        },
      ];
    case GSMProcessKind.Delivery:
      return [
        {
          label: t("Allgemein"),
          items: [Overview, Contacts],
        },
        {
          label: t("Meilensteine"),
          items: [
            {
              ...WorkOrders,
              children: WorkOrders.children?.filter(itemFilter),
            },
            Certificates,
          ].filter(itemFilter),
        },
      ];
    default:
      return [];
  }
}
